<script setup lang="ts">
import { computed, onBeforeUnmount, watch } from 'vue'
import { statusBarHeight } from '@/utils/keep-alive-config'
import defaultAiIcon from '@/assets/images/message/app-inner-alert-ai-icon.png'

defineOptions({
  name: 'AppInnerAlert',
})

interface Props {
  modelValue: boolean
  icon?: string
  title: string
  content: string
  duration?: number
}

const props = withDefaults(defineProps<Props>(), {
  icon: defaultAiIcon,
  duration: 5000,
})

const emit = defineEmits<{
  (event: 'update:modelValue', value: boolean): void
  (event: 'click'): void
}>()

let autoDismissTimer: ReturnType<typeof setTimeout> | null = null

const alertStyle = computed(() => ({
  '--app-inner-alert-top': `${Math.max(0, statusBarHeight.value) + 10}px`,
}))

const clearAutoDismissTimer = () => {
  if (autoDismissTimer === null) return
  clearTimeout(autoDismissTimer)
  autoDismissTimer = null
}

const dismiss = () => {
  clearAutoDismissTimer()
  emit('update:modelValue', false)
}

const scheduleAutoDismiss = () => {
  clearAutoDismissTimer()
  if (!props.modelValue || props.duration <= 0) return
  autoDismissTimer = setTimeout(dismiss, props.duration)
}

const handleClick = () => {
  dismiss()
  emit('click')
}

watch(
  () => [props.modelValue, props.duration],
  scheduleAutoDismiss,
  { immediate: true },
)

onBeforeUnmount(clearAutoDismissTimer)
</script>

<template>
  <Transition name="app-inner-alert">
    <button
      v-if="modelValue"
      class="app-inner-alert"
      type="button"
      :style="alertStyle"
      @click="handleClick"
    >
      <img class="app-inner-alert__icon" :src="icon" alt="" />
      <span class="app-inner-alert__text">
        <strong class="app-inner-alert__title">{{ title }}</strong>
        <span class="app-inner-alert__content">{{ content }}</span>
      </span>
    </button>
  </Transition>
</template>

<style lang="scss" scoped>
.app-inner-alert {
  position: fixed;
  top: var(--app-inner-alert-top);
  left: 16px;
  right: 16px;
  z-index: 1000;
  display: flex;
  align-items: center;
  width: auto;
  height: 130px;
  padding: 26px 36px;
  overflow: hidden;
  border: 2px solid #FFFFFF;
  border-radius: 40px;
  background: linear-gradient( 135deg, rgba(206,213,255,0.5) 0%, rgba(237,239,255,0.66) 42.7%, rgba(255,255,255,0.76) 100%);
  box-shadow: 0px 10px 12px 0px rgba(127,145,180,0.16);
  color: #141c28;
  text-align: left;
  box-sizing: border-box;
  appearance: none;
  -webkit-appearance: none;
  backdrop-filter: blur(24px) saturate(125%);
  -webkit-backdrop-filter: blur(24px) saturate(125%);
  transform-origin: top center;
  will-change: transform, opacity;
  -webkit-tap-highlight-color: transparent;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    border-radius: inherit;
    box-shadow: inset 0 -12px 24px rgba(147, 114, 255, 0.05);
  }

  &:active {
    transform: scale(0.985);
  }
}

.app-inner-alert__icon {
  position: relative;
  z-index: 1;
  display: block;
  flex: 0 0 58px;
  width: 58px;
  height: 58px;
  object-fit: contain;
}

.app-inner-alert__text {
  position: relative;
  z-index: 1;
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  padding-left: 24px;
}

.app-inner-alert__title {
  overflow: hidden;
  color: #141c28;
  font-family: 'PingFang SC', sans-serif;
  font-size: 26px;
  font-weight: 500;
  line-height: 34px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.app-inner-alert__content {
  display: -webkit-box;
  margin-top: 8px;
  overflow: hidden;
  color: #141c28;
  font-family: 'PingFang SC', sans-serif;
  font-size: 28px;
  font-weight: 400;
  line-height: 36px;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
}

.app-inner-alert-enter-active {
  transition:
    transform 460ms cubic-bezier(0.22, 1.08, 0.36, 1),
    opacity 220ms ease-out;
}

.app-inner-alert-leave-active {
  pointer-events: none;
  transition:
    transform 260ms cubic-bezier(0.4, 0, 1, 1),
    opacity 200ms ease-in;
}

.app-inner-alert-enter-from,
.app-inner-alert-leave-to {
  transform: translate3d(0, calc(-100% - var(--app-inner-alert-top) - 16px), 0) scale(0.98);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .app-inner-alert-enter-active,
  .app-inner-alert-leave-active {
    transition-duration: 1ms;
  }
}
</style>
